import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import { getCroppedImg } from "../utils/cropImage";
import { UserCircle2, X, Eye, EyeOff, Upload, Camera, Save, RefreshCw } from "lucide-react";
import Select from "react-select";
import { getAllDepartments } from "../services/departmentService";
import { defaultUserImg } from "../assets/defaultUser";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";
import { fetchBookings } from "../actions/BookingActions";

const AdminUserUpdate = ({ isOpen, onClose, serviceNo, onSuccess }) => {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((s) => s.auth);

  const [formData, setFormData] = useState({
    ServiceNo: "",
    Name: "",
    MobileNo: "",
    role: "E",
    status: "A",
    department_code: "",
    password: "",
    file_path: null,
  });

  const [previewImage, setPreviewImage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);

  const roles = [
    { value: "S", label: "Supervisor" },
    // { value: "E", label: "Employee" },
    { value: "A", label: "Admin" },
    { value: "EN", label: "Engineer" },
    { value: "D", label: "Driver" },
  ];

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
  ];

  const mapRole = (r) => {
    if (!r) return "E";
    const v = r.toString().trim().toUpperCase();
    switch (v) {
      case "S": case "SUPERVISOR": return "S";
      // case "E": case "EMPLOYEE": return "E";
      case "A": case "ADMIN": return "A";
      case "EN": case "ENGINEER": return "EN";
      case "D": case "DRIVER": return "D";
      default: return "E";
    }
  };

  const mapStatus = (s) => {
    const v = s?.toString().trim().toUpperCase();
    return v === "ACTIVE" || v === "A" ? "A" : "I";
  };

  const getUserImgUrl = (sno) =>
    `https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${sno}`;

  useEffect(() => {
    if (!isOpen || !serviceNo) return;

    const loadDept = async () => {
      try {
        const data = await getAllDepartments();
        setDepartments(data || []);
      } catch (err) {
        console.error("Error loading departments", err);
      }
    };
    loadDept();

    const fetchUser = async () => {
      setFetching(true);
      setMessage("");
      try {
        const users = await dispatch(fetchUsers());
        const user = users.find(
          (u) => u.ServiceNo?.toString().trim() === serviceNo?.toString().trim()
        );
        if (!user) {
          setMessage("❌ User not found.");
        } else {
          setFormData({
            ServiceNo: user.ServiceNo,
            Name: user.Name,
            MobileNo: user.MobileNo,
            role: mapRole(user.role),
            status: mapStatus(user.status),
            department_code: user.department || user.Department || "",
            password: user.password || "",
            file_path: user.file_path || null,
          });
          setPreviewImage(getUserImgUrl(user.ServiceNo) + `&t=${Date.now()}`);
        }
      } catch (err) {
        console.error("User fetch error:", err);
        setMessage("❌ Failed to fetch user.");
      } finally {
        setFetching(false);
      }
    };

    fetchUser();
  }, [serviceNo, authKey, dispatch, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "MobileNo" && !/^\d*$/.test(value)) return;
    if (name === "Name" && !/^[a-zA-Z\s]*$/.test(value)) return;

    setFormData((p) => ({ ...p, [name]: value }));
    setMessage("");
  };

  const handleDeptChange = (option) => {
    setFormData((p) => ({ ...p, department_code: option ? option.value : "" }));
    setMessage("");
  };

  const departmentOptions = departments.map((d) => ({ 
    value: d.code || d.name || "", 
    label: d.name || d.code || "" 
  }));

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("❌ Please select an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(file);
      setPreviewImage(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const applyCrop = useCallback(async () => {
    if (!croppedAreaPixels || !previewImage) return;
    try {
      const blob = await getCroppedImg(previewImage, croppedAreaPixels);
      setProfileImageBlob(blob);
      setPreviewImage(URL.createObjectURL(blob));
      setShowCropModal(false);
      setSelectedFile(null);
      setMessage("");
    } catch (err) {
      console.error("applyCrop error:", err);
      setMessage("❌ Failed to crop image.");
    }
  }, [croppedAreaPixels, previewImage]);

  const cancelCrop = () => {
    if (formData.ServiceNo) {
      setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
    } else {
      setPreviewImage("");
    }
    setSelectedFile(null);
    setProfileImageBlob(null);
    setShowCropModal(false);
    setMessage("");
  };

  const uploadImageToServer = async (serviceNoLocal, blobOrFile) => {
    const file = blobOrFile instanceof File ? blobOrFile : new File([blobOrFile], `${serviceNoLocal}.jpg`, { type: "image/jpeg" });

    const fd = new FormData();
    fd.append("ServiceNo", serviceNoLocal);
    fd.append("ImageFile", file);

    const uploadUrl = "https://aeroviewbackend.dockyardsoftware.com/User/Fileupload";
    const resp = await axios.post(uploadUrl, fd, {
      headers: { "Content-Type": "multipart/form-data", "auth-key": authKey || "" },
    });
    return resp;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    if (!/^\d{10}$/.test(formData.MobileNo)) {
      setMessage("❌ Mobile number must be 10 digits.");
      setLoading(false);
      return;
    }

    // Check if driver is assigned to a vehicle
    try {
      const vehicles = await dispatch(fetchVehicles());
      const assignedVehicle = vehicles.find(
        (v) => v.driverSno?.toString().trim() === formData.ServiceNo?.toString().trim()
      );

      if (assignedVehicle) {
        setMessage(`❌ Cannot update: Driver is assigned to vehicle ${assignedVehicle.vehicleId}. Please unassign first.`);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Vehicle check error:", err);
      setMessage("❌ Failed to verify driver assignment status.");
      setLoading(false);
      return;
    }

    // Check for active bookings
    try {
      const allBookings = await dispatch(fetchBookings());
      const userBookings = allBookings.filter(
        (b) => b.bookingSno?.toString().trim() === formData.ServiceNo?.toString().trim()
      );

      const activeBooking = userBookings.find((b) => {
        const s = b.status?.toString().trim().toUpperCase();
        if (!s) return false;
        const allowedStatuses = ["C", "CANCEL", "CO", "COMPLETED"];
        return !allowedStatuses.includes(s);
      });

      if (activeBooking) {
        setMessage(`❌ Cannot update: User has an active booking (ID: ${activeBooking.bId}, Status: ${activeBooking.status}).`);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Booking check error:", err);
      setMessage("❌ Failed to verify booking status.");
      setLoading(false);
      return;
    }

    try {
      const userData = {
        ServiceNo: formData.ServiceNo,
        Name: formData.Name,
        MobileNo: formData.MobileNo,
        role: formData.role,
        status: formData.status,
        password: formData.password,
        created_by: "Admin",
        updated_by: "Admin",
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        department_code: formData.department_code,
      };

      const postResp = await axios.post(
        "https://aeroviewbackend.dockyardsoftware.com/UserRegister/PostUserDetails",
        [userData],
        { headers: { "Content-Type": "application/json", "auth-key": authKey || "" } }
      );

      if (postResp?.data?.statusCode !== 200) {
        throw new Error(postResp?.data?.result || "Failed to update user");
      }

      if (profileImageBlob) {
        await uploadImageToServer(formData.ServiceNo, profileImageBlob);
        setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
        setProfileImageBlob(null);
      }

      setMessage("✅ User updated successfully.");
      if (onSuccess) onSuccess();
      setTimeout(() => {
        setMessage("");
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Submit error:", err);
      setMessage("❌ Network or API error.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isCurrentUser = userDetails?.ServiceNo?.toString() === formData.ServiceNo?.toString();

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-50 rounded-lg">
                <UserCircle2 size={16} className="text-blue-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Update User</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Message Alert */}
            {message && (
              <div className={`mb-4 p-3 rounded-lg text-xs ${
                message.includes("✅") 
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {message}
              </div>
            )}

            {fetching ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="text-gray-400 animate-spin" />
                <span className="ml-2 text-xs text-gray-500">Loading user details...</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Profile Image */}
                <div className="flex flex-col items-center gap-2">
                  <div className="relative group">
                    <img
                      src={previewImage || defaultUserImg}
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 group-hover:border-gray-300 transition"
                      alt="profile"
                      onError={(e) => { e.currentTarget.src = defaultUserImg; }}
                    />
                    <div 
                      className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera size={20} className="text-white" />
                    </div>
                    {profileImageBlob && (
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition"
                        onClick={() => {
                          setProfileImageBlob(null);
                          setPreviewImage(getUserImgUrl(formData.ServiceNo) + `&t=${Date.now()}`);
                        }}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Upload size={10} />
                    Upload Photo
                  </button>
                </div>

                {/* Service No (Read Only) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Service No
                  </label>
                  <input
                    type="text"
                    name="ServiceNo"
                    value={formData.ServiceNo}
                    readOnly
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="Name"
                    value={formData.Name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
                  />
                </div>

                {/* Mobile No */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="MobileNo"
                    value={formData.MobileNo}
                    onChange={handleChange}
                    required
                    maxLength={10}
                    pattern="[0-9]{10}"
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
                  />
                </div>

                {/* Role and Status */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      disabled={isCurrentUser}
                      className={`w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition ${
                        isCurrentUser ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
                      }`}
                    >
                      {roles.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={isCurrentUser}
                      className={`w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition ${
                        isCurrentUser ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
                      }`}
                    >
                      {statuses.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Department */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <Select
                    options={departmentOptions}
                    value={departmentOptions.find((o) => o.value === formData.department_code) || null}
                    onChange={handleDeptChange}
                    placeholder="Select department"
                    isClearable
                    className="text-sm"
                    classNamePrefix="react-select"
                    isDisabled={isCurrentUser}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#e5e7eb',
                        borderRadius: '0.5rem',
                        minHeight: '36px',
                        boxShadow: 'none',
                        '&:hover': { borderColor: '#9ca3af' },
                      }),
                    }}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current"
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition pr-8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Leave empty to keep current password</p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save size={14} />
                        Update
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                  <Camera size={16} className="text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">Crop Image</h2>
              </div>
              <button
                onClick={cancelCrop}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={16} className="text-gray-400" />
              </button>
            </div>

            <div className="p-4">
              <div className="relative h-64 bg-gray-100 rounded-lg overflow-hidden">
                <Cropper
                  image={previewImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Zoom</p>
                <Slider 
                  value={zoom} 
                  min={1} 
                  max={3} 
                  step={0.1} 
                  onChange={(e, v) => setZoom(v)} 
                  size="small"
                  sx={{
                    color: '#1f2937',
                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                  }}
                />
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={cancelCrop}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowCropModal(false); setSelectedFile(null); setTimeout(() => fileInputRef.current?.click?.(), 120); }}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition"
                  type="button"
                >
                  Change
                </button>
                <button
                  onClick={applyCrop}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition"
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUserUpdate;